import { BiddingIntradayOrderStatus } from '@ebalance/proto/bffuser/v1/bidding_common_model_pb'
import {
  BiddingIntradayOrderWithdrawFailureReason,
  BiddingIntradayOrderWithdrawState,
} from '@ebalance/proto/bffuser/v1/bidding_intraday_model_pb'
import type { TFunction } from 'i18next'

import type {
  BulkResultNoticeSpec,
  BulkToast,
} from '~/hooks/bidding/useBulkResultNotice'
import type { BulkOutcome } from '~/models/bulkResultNotice'

/**
 * 一括板取消 (AC-BIN-112) の結果トーストに使う札の値 (一覧項目の一部)。
 * 時間前の一覧の観測 (`IntradayWithdrawNoticeProvider` の `orders`) がそのまま渡る。
 */
export type IntradayBulkWithdrawNoticeOrder = {
  id: string
  withdrawState?: BiddingIntradayOrderWithdrawState
  withdrawFailureReason?: BiddingIntradayOrderWithdrawFailureReason
  bidStatus?: BiddingIntradayOrderStatus
  withdrawable?: boolean
}

/** 失敗が確定した理由 (AC-BIN-008b の 3 つ)。 */
export type IntradayBulkWithdrawFailure = 'retryLimit' | 'filled' | 'gateClosed'

const FAILURE_OF: Record<
  BiddingIntradayOrderWithdrawFailureReason,
  IntradayBulkWithdrawFailure
> = {
  // 理由の付かない失敗確定は、 1 件の板取消と同じく札が板に残っている側 (リトライ限界) に倒す
  // (`withdrawFailureText.ts`)。
  [BiddingIntradayOrderWithdrawFailureReason.UNSPECIFIED]: 'retryLimit',
  [BiddingIntradayOrderWithdrawFailureReason.RETRY_LIMIT]: 'retryLimit',
  [BiddingIntradayOrderWithdrawFailureReason.FILLED]: 'filled',
  [BiddingIntradayOrderWithdrawFailureReason.GATE_CLOSED]: 'gateClosed',
}

/**
 * 一括で受け付けた札の、 その観測での結果 (AC-BIN-112)。
 * - 取消処理中 (PENDING) → 処理中
 * - 失敗確定 (FAILED) → 失敗 (理由つき)
 * - 取消 / 自動取消、 または一部約定で板に残りが無い (取下不可・取消の進行状態なし) → 成立
 *   (AC-BIN-035: 一部約定の札は残りを取り消して「部分約定」 になる)
 * - それ以外 (まだ取消の行が拾われていない入札中 等) → 分からないので追い続ける
 */
export function classifyIntradayBulkWithdraw(
  order: IntradayBulkWithdrawNoticeOrder,
): BulkOutcome<IntradayBulkWithdrawFailure> {
  if (order.withdrawState === BiddingIntradayOrderWithdrawState.PENDING) {
    return 'pending'
  }
  if (order.withdrawState === BiddingIntradayOrderWithdrawState.FAILED) {
    return {
      failed:
        FAILURE_OF[
          order.withdrawFailureReason ??
            BiddingIntradayOrderWithdrawFailureReason.UNSPECIFIED
        ],
    }
  }
  if (
    order.bidStatus === BiddingIntradayOrderStatus.CANCELLED ||
    order.bidStatus === BiddingIntradayOrderStatus.AUTO_CANCELLED
  ) {
    return 'succeeded'
  }
  const noWithdrawState =
    order.withdrawState === undefined ||
    order.withdrawState === BiddingIntradayOrderWithdrawState.UNSPECIFIED
  if (
    order.bidStatus === BiddingIntradayOrderStatus.PARTIAL_MATCHED &&
    order.withdrawable === false &&
    noWithdrawState
  ) {
    return 'succeeded'
  }
  return 'unknown'
}

/**
 * 失敗のトーストの本文。 `filledOrGateClosed` は全量約定と GC 到達が混ざる時、
 * `retryLimitMixed` はリトライ限界と (全量約定・GC 到達) が混ざる時の本文。
 */
export type IntradayBulkWithdrawFailureBody = {
  key: IntradayBulkWithdrawFailure | 'filledOrGateClosed' | 'retryLimitMixed'
  /** そのトーストで知らせる札の件数。 */
  count: number
}

/**
 * 失敗のトーストの本文を、 理由の組で選ぶ (AC-BIN-112)。 リトライ限界だけ / 全量約定だけ /
 * GC 到達だけ / 全量約定と GC 到達 / リトライ限界と (全量約定・GC 到達) の 5 通り。
 * 件数はどれもそのトーストで知らせる札の数。 札が無ければ undefined。
 */
export function intradayBulkWithdrawFailureBody(
  reasons: readonly IntradayBulkWithdrawFailure[],
): IntradayBulkWithdrawFailureBody | undefined {
  const count = reasons.length
  if (count === 0) return undefined
  const has = (r: IntradayBulkWithdrawFailure): boolean => reasons.includes(r)
  const ended = has('filled') || has('gateClosed')
  if (has('retryLimit')) {
    return { key: ended ? 'retryLimitMixed' : 'retryLimit', count }
  }
  if (has('filled') && has('gateClosed')) {
    return { key: 'filledOrGateClosed', count }
  }
  return { key: has('filled') ? 'filled' : 'gateClosed', count }
}

const KEY = 'page:biddingIntraday.bulkWithdraw'

/**
 * 時間前の一括板取消の結果トースト (一括操作の結果トーストに渡す市場ごとの部分)。
 * 文言は AC-BIN-112 (処理中の見出しは AC-BIN-008 の「取消を受け付けました (処理中)」)。
 * 出し方は一括ごとに成功 1 通・失敗 1 通 (AC-BIN-112「件数をまとめた 1 通」。 確定の都度に
 * 出すかは PdM確認 #12392)。
 */
export function intradayBulkWithdrawNoticeSpec<
  T extends IntradayBulkWithdrawNoticeOrder,
>(t: TFunction): BulkResultNoticeSpec<T, IntradayBulkWithdrawFailure> {
  return {
    keyOf: (order) => order.id,
    classify: classifyIntradayBulkWithdraw,
    emit: 'oncePerBatch',
    successToast: (orders): BulkToast => ({
      type: 'success',
      title: t(`${KEY}.successToast.title`),
      description: t(`${KEY}.successToast.body`, { count: orders.length }),
    }),
    // 見出しは 1 件の板取消 (AC-BIN-008) と同じ。 本文は送るのを待っている札と自動リトライ中の札の
    // 両方に当たるので、 リトライの本文ではなく一括の本文にする。
    pendingToast: (): BulkToast => ({
      type: 'warning',
      title: t('page:biddingIntraday.withdrawPendingToast.title'),
      description: t(`${KEY}.pendingToast.body`),
    }),
    failureToast: (failures): BulkToast => {
      const body = intradayBulkWithdrawFailureBody(
        failures.map((f) => f.reason),
      )
      return {
        type: 'error',
        title: t('page:biddingIntraday.withdrawFailedToast.title'),
        description:
          body === undefined
            ? undefined
            : t(`${KEY}.failedToast.${body.key}`, { count: body.count }),
      }
    },
  }
}
